import { gql, useLazyQuery } from '@apollo/client';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { GetSummaryUserQuoteInput, Query } from 'generated/graphql';
import { useCallback } from 'react';
import { useGetUser } from 'v2/core/services/user';

type GetSummaryUserQuoteParams = Omit<
  GetSummaryUserQuoteInput,
  'tenantID' | 'userID'
>;

const GET_SUMMARY_USER_QUOTE_QERY = gql`
  query getSummaryUserQuote(
    $tenantID: ID!
    $userID: ID!
    $sessionID: ID!
    $recordID: ID!
    $labelID: ID
    $claim: String
  ) {
    getSummaryUserQuote(
      request: {
        tenantID: $tenantID
        userID: $userID
        sessionID: $sessionID
        recordID: $recordID
        labelID: $labelID
        claim: $claim
      }
    ) {
      responseID
      source
      sourceIcon
      externalLink
      startTime
      endTime
      labelID
    }
  }
`;

export function useGetSummaryUserQuote() {
  const {
    id: userID,
    tenant: { id: tenantID },
  } = useGetUser();

  const { showErrorNotification } = useNotificationContext();

  const [lazyFunction, { loading, data }] = useLazyQuery<
    Query,
    GetSummaryUserQuoteInput
  >(GET_SUMMARY_USER_QUOTE_QERY, {
    fetchPolicy: 'network-only',
    notifyOnNetworkStatusChange: false,
    onError: () => {
      showErrorNotification({
        contentComponent: 'Failed to get the summary user quote.',
        props: {},
      });
    },
  });

  const getSummaryUserQuote = useCallback(
    ({ sessionID, labelID, recordID, claim }: GetSummaryUserQuoteParams) => {
      lazyFunction({
        variables: {
          sessionID,
          tenantID,
          userID,
          recordID,
          labelID,
          claim,
        },
      });
    },
    [tenantID, userID, lazyFunction],
  );

  return [
    getSummaryUserQuote,
    {
      isLoading: loading,
      summaryUserQuote: data?.getSummaryUserQuote,
    },
  ] as const;
}
